import React from 'react';
import { Box, Grid, SvgIcon, Typography } from '@mui/material';
import { useGlobalState } from '../../../utils/GlobalStateProvider';
import RootineButton from '../../../atoms/RootineButton';
import { RootineImage } from '../../../atoms/RootineImage';
import { AccordionCard } from '../../Library/AccordionCard';
import { ReactComponent as RecommendationIcon } from '../../../images/Take-final.svg';
import { colors } from '../../../utils/theme';
import { BloodInsightRecommendationDetails } from '../../../types';
import { Product } from '../../../cms/types';

export const BloodRecommendations = ({
  recommendations,
}: {
  recommendations: BloodInsightRecommendationDetails[];
}): JSX.Element => {
  const { state, addToCart } = useGlobalState(true);
  const hasVitaminSubscription = state.user.subscription_status === 'active';
  const getProduct = (title: string) => {
    return state.products.size > 0 ? state.products.get(title) : undefined;
  };

  const handleAddProduct = (product: Product) => {
    addToCart(product);
  };

  const isProductInCart = (productCode: string) => {
    return !!state?.cart?.find((item) => item?.code === productCode);
  };

  return (
    <Box>
      {recommendations?.map((recommendationType, index: number) => (
        <Box key={`${recommendationType.label}-${index}`} sx={{ mb: 2 }}>
          <AccordionCard
            panelId="blood-test-recommendations"
            summary={
              <Grid
                container
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Grid
                  container
                  flexDirection="row"
                  alignItems="center"
                  item
                  xs={7}
                  sm={5}
                >
                  <SvgIcon
                    component={RecommendationIcon}
                    inheritViewBox
                    sx={{
                      width: 16,
                      height: 16,
                      mr: 0.5,
                      fill: colors.greys.BASE_WHITE,
                    }}
                  />
                  <Typography variant="light" fontSize={14}>
                    RECOMMENDATIONS
                  </Typography>
                </Grid>
                <Grid
                  container
                  justifyContent="flex-end"
                  item
                  xs={4}
                  sm={5}
                  sx={{ mr: 1 }}
                >
                  <Typography fontSize={16}>
                    {recommendationType.label}
                  </Typography>
                </Grid>
              </Grid>
            }
            details={
              <>
                {recommendationType.isPurchaseRec ? (
                  <Box display="flex" flexDirection="column" sx={{ mt: 2 }}>
                    {recommendationType.description && (
                      <Typography
                        variant="light"
                        fontSize={14}
                        sx={{ mt: 1, whiteSpace: 'pre-line' }}
                      >
                        {recommendationType.description}
                      </Typography>
                    )}

                    {recommendationType.recs.map((rec, index) => {
                      const product = getProduct(rec.title);

                      return (
                        <Box
                          display="flex"
                          flexDirection="column"
                          sx={{ mt: 3, mb: 3 }}
                          key={`${rec.title}-${index}`}
                        >
                          {product && (
                            <>
                              {rec.description && (
                                <Typography variant="light" fontSize={14}>
                                  {rec.description}
                                </Typography>
                              )}

                              <Box
                                display="flex"
                                flexDirection="row"
                                sx={{ my: 3 }}
                              >
                                {product?.heroImage[0].src && (
                                  <Box>
                                    <RootineImage
                                      src={product?.heroImage[0].src}
                                      size="sm"
                                      alt={`${recommendationType.label}-image`}
                                      fit="cover"
                                    />
                                  </Box>
                                )}

                                <Box
                                  display="flex"
                                  flexDirection="column"
                                  sx={{ ml: 3, width: '60%' }}
                                >
                                  <Typography
                                    variant="medium"
                                    fontSize={18}
                                    sx={{ mb: 1 }}
                                  >
                                    {product?.title}
                                  </Typography>
                                  {rec?.secondary_description && (
                                    <Typography variant="light" fontSize={14}>
                                      {rec?.secondary_description}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>

                              <RootineButton
                                variation="primary"
                                size="medium"
                                label={
                                  isProductInCart(product.code)
                                    ? 'In your cart'
                                    : hasVitaminSubscription
                                    ? 'Already a subscriber'
                                    : `Add to cart - $${product?.price?.toFixed(
                                        2
                                      )}${product?.shortTextPrice ?? ''}`
                                }
                                onClick={() => handleAddProduct(product)}
                                disabled={
                                  isProductInCart(product.code) ||
                                  hasVitaminSubscription
                                }
                              />
                            </>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Box display="flex" flexDirection="column" sx={{ mt: 2 }}>
                    {recommendationType.description && (
                      <Typography
                        variant="light"
                        fontSize={14}
                        sx={{ mt: 1, whiteSpace: 'pre-line' }}
                      >
                        {recommendationType.description}
                      </Typography>
                    )}

                    {recommendationType.recs.map((rec, index) => (
                      <Box
                        display="flex"
                        flexDirection="column"
                        key={`${recommendationType.label}-${index}`}
                      >
                        {rec.imgSrc && (
                          <RootineImage
                            src={rec.imgSrc}
                            height={260}
                            alt={`${rec.title}-image`}
                            fit="cover"
                          />
                        )}

                        {rec.goal && (
                          <Typography
                            variant="medium"
                            fontSize={18}
                            sx={{ my: 1.5 }}
                          >
                            Goal: {rec.goal}
                          </Typography>
                        )}

                        {rec.description && (
                          <Typography
                            variant="light"
                            fontSize={14}
                            sx={{ whiteSpace: 'pre-line' }}
                          >
                            {rec.description}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            }
          />
        </Box>
      ))}
    </Box>
  );
};
